import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { WorkflowOrganisationAccessLevel } from "../../../../../constants";
import { parseProjectPhase } from "../../../../../constants/project-phase";
import { TaskConstants } from "../../../../../constants/workflow/TaskConstants";
import { Origin, ProjectDatesData, UpcomingMilestonePropsWithKey, WorkflowOrganisation } from "../../../../../models";
import { IStateOrigin } from "../../../../../navigators/navigateStateObjects";
import {
  getAggregateWorkflowTasks,
  GetAggregateWorkflowTasksResponse,
  GetTaskBoardResponse,
  searchWorkflowTasks,
  SearchWorkflowTasksResponse,
} from "../../../../../service/query";
import { Status } from "../../../../../service/Shared";
import { useDeepLinkedTask } from "../../../../../templates/WorkflowTaskPanel";
import { useAuth } from "../../../../../useAuth";
import { getTaskParentObjectNavigation } from "../../../../../utils";
import {
  getDashboardRoute,
  getOperationsDealWorkflowsRoute,
  getProjectTasksRoute,
  getProjectTrackerRoute,
  getWorkflowTemplatesRoute,
} from "../../../../../utils/routes";
import { dataGridMapFilterCriteria, ProfileMenuItemType } from "../../../../../widget";
import { DealStageItem } from "../../../../asset-manager/deal/components";
import { TasksBoardContext } from "./TasksBoardContext";

interface useTasksBoardReturnData {
  projectUuid: string | undefined;
  objectUuid: string | undefined;
  objectName: string | undefined;
  objectRowVersion: number | undefined;
  workflowUuid: string | undefined;
  tasksBoard: GetTaskBoardResponse | undefined;
  workflowOrganisationAccessLevel: WorkflowOrganisationAccessLevel | undefined;
  workflowOrganisations: WorkflowOrganisation[] | undefined;
  workflow: GetTaskBoardResponse["results"][number] | undefined;
  isDealWorkflow: boolean;
  dealStages: DealStageItem[];
  currentDealStage: string | undefined;
  handleDealStageChange: (stage: string) => void;

  showComplete: boolean;
  handleShowCompleteChange: () => void;

  showAddWorkflowStepPanel: boolean;
  setShowAddWorkflowStepPanel: Dispatch<SetStateAction<boolean>>;

  showRemoveWorkflowModal: boolean;
  setShowRemoveWorkflowModal: Dispatch<SetStateAction<boolean>>;

  showEditDatesPanel: boolean;
  closeEditDatesPanel: () => void;
  handleDealDatesSaved: () => void;

  projectDates: ProjectDatesData | undefined;
  setProjectDates: Dispatch<SetStateAction<ProjectDatesData | undefined>>;

  showUpcomingMilestonesPanel: boolean;
  setShowUpcomingMilestonesPanel: Dispatch<SetStateAction<boolean>>;

  showWorkflowTaskPanel: boolean;
  setShowWorkflowTaskPanel: Dispatch<SetStateAction<boolean>>;

  selectedPanelTask: SearchWorkflowTasksResponse["results"][0] | undefined;
  setSelectedPanelTask: (task: SearchWorkflowTasksResponse["results"][0] | undefined) => void;

  quickActionsMenuOptionsList: ProfileMenuItemType[];
  showQuickActionsMenu: boolean;
  setShowQuickActionsMenu: Dispatch<SetStateAction<boolean>>;

  refreshTasksBoard: boolean;
  shouldRefreshTasksBoard: () => void;

  handleCloseWorkflowTaskPanel: () => void;
  hasAccessLevelAndCanEditTemplate: (accessLevel: WorkflowOrganisationAccessLevel) => boolean;
  handleBackClick: () => void;
  goBackText: string;
  upcomingMilestones: UpcomingMilestonePropsWithKey[];
  isLoadingMilestones: boolean;
  hasMilestonesError: boolean;

  toggleProgress: boolean;
  taskProgress: GetAggregateWorkflowTasksResponse | undefined;
  onToggle: (value: boolean) => void;

  showWorkflowPermissionsPanel: boolean;
  setShowWorkflowPermissionsPanel: Dispatch<SetStateAction<boolean>>;
}

export const useTasksBoard = (): useTasksBoardReturnData => {
  const navigate = useNavigate();
  const { state, pathname, search } = useLocation();
  const stateOrigin = state?.origin as IStateOrigin;
  const [searchParams] = useSearchParams();
  const origin = searchParams.get("origin");

  const { currentUserType } = useAuth();
  const {
    projectUuid,
    workflowUuid,
    tasksBoard,
    workflowOrganisationAccessLevel,
    workflowOrganisations,
    projectDates,
    setProjectDates,
    showEditDatesPanel,
    setShowEditDatesPanel,
    selectedWorkflowStage,
    isDealWorkflow,
    dealStages,
    currentDealStage,
    handleDealStageChange,
    showComplete,
    handleShowCompleteChange,
    shouldRefreshTasksBoard,
    refreshTasksBoard,
    hasAccessLevelAndCanEditTemplate,
    boardView,
  } = useContext(TasksBoardContext);

  let goBackText = stateOrigin?.goBackText;

  if (!goBackText) {
    switch (origin) {
      case Origin.ProjectTasksPage:
        goBackText = "Back to project";
        break;
      case Origin.DashboardPage:
        goBackText = "Back to dashboard";
        break;
      case Origin.ProjectTrackerPage:
        goBackText = "Back to projects";
        break;
      default:
        goBackText = "Back to workflows";
        break;
    }
  }

  const handleBackClick = (): void => {
    if (stateOrigin?.from) {
      navigate(stateOrigin?.from);
      return;
    }
    switch (origin) {
      case Origin.ProjectTasksPage:
        navigate(getProjectTasksRoute(tasksBoard?.results[0].projectUuid || "", currentUserType));
        break;
      case Origin.DashboardPage:
        navigate(getDashboardRoute(currentUserType));
        break;
      case Origin.ProjectTrackerPage:
        navigate(getProjectTrackerRoute(currentUserType));
        break;
      default:
        navigate(
          isDealWorkflow ? getOperationsDealWorkflowsRoute(currentUserType) : getWorkflowTemplatesRoute(currentUserType)
        );
        break;
    }
  };

  const [showQuickActionsMenu, setShowQuickActionsMenu] = useState(false);
  const [showAddWorkflowStepPanel, setShowAddWorkflowStepPanel] = useState(false);
  const [showRemoveWorkflowModal, setShowRemoveWorkflowModal] = useState(false);
  const [upcomingMilestones, setUpcomingMilestones] = useState<UpcomingMilestonePropsWithKey[]>([]);
  const [isLoadingMilestones, setIsLoadingMilestones] = useState(true);
  const [hasMilestonesError, setHasMilestonesError] = useState(false);
  /** Identifies the newest milestone fetch, so a slower earlier one cannot overwrite its result. */
  const milestonesRequestId = useRef(0);
  const [showUpcomingMilestonesPanel, setShowUpcomingMilestonesPanel] = useState(false);
  const [toggleProgress, setToggleProgress] = useState(false);
  const [taskProgress, setTaskProgress] = useState<GetAggregateWorkflowTasksResponse | undefined>();
  const [showWorkflowPermissionsPanel, setShowWorkflowPermissionsPanel] = useState(false);
  const [selectedPanelTask, setSelectedPanelTask] = useState<SearchWorkflowTasksResponse["results"][0]>();
  const [showWorkflowTaskPanel, setShowWorkflowTaskPanel] = useState(false);

  /*
    A notification for a workflow task links to its board naming the task in ?taskUuid= (KAN-9609), so the
    user lands on the task itself rather than hunting for its card among the phase's steps. The General
    view has its own grid and resolves the link through useTasksGeneralView, so this owner stands down
    there — one view resolves the uuid at a time, giving one request and one overlay.
  */
  const deepLinkedTask = useDeepLinkedTask(boardView !== "general");
  useEffect(() => {
    if (!deepLinkedTask) return;
    setSelectedPanelTask(deepLinkedTask);
    setShowWorkflowTaskPanel(true);
  }, [deepLinkedTask]);

  const editDatesAction = (): void => {
    setShowEditDatesPanel(true);
  };

  // Undefined where this role has no page for the object, e.g. a Developer and a deal.
  const viewObjectAction = getTaskParentObjectNavigation(
    isDealWorkflow ? "deal" : "project",
    isDealWorkflow ? tasksBoard?.results[0].dealUuid : tasksBoard?.results[0].projectUuid,
    currentUserType,
    navigate,
    { goBackText: "Back to tasks board", from: `${pathname}${search}` }
  );

  const quickActionsMenuOptionsList: ProfileMenuItemType[] = [
    ...(hasAccessLevelAndCanEditTemplate(WorkflowOrganisationAccessLevel.Owner)
      ? [
          ...(!isDealWorkflow
            ? [
                {
                  id: 1,
                  value: "Edit access",
                  action: () => setShowWorkflowPermissionsPanel(true),
                },
              ]
            : []),
          {
            id: 2,
            value: isDealWorkflow ? "Edit stage dates" : "Edit phase dates",
            action: editDatesAction,
          },
          {
            id: 3,
            value: "Remove workflow",
            action: () => setShowRemoveWorkflowModal(true),
          },
        ]
      : [
          {
            id: 1,
            value: isDealWorkflow ? "Edit stage dates" : "Edit phase dates",
            action: editDatesAction,
          },
        ]),
    ...(viewObjectAction
      ? [
          {
            id: 4,
            value: isDealWorkflow ? "View deal" : "View project",
            action: viewObjectAction,
          },
        ]
      : []),
  ];

  const handleCloseWorkflowTaskPanel = useCallback((): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  }, []);

  const searchUpcomingMilestoneTasks = async (): Promise<void> => {
    // Only the newest fetch may touch milestone state — the board refreshes on task changes and on
    // workflow switches, either of which can start another while this one is still in flight.
    milestonesRequestId.current += 1;
    const requestId = milestonesRequestId.current;
    const isCurrentRequest = (): boolean => requestId === milestonesRequestId.current;

    setIsLoadingMilestones(true);
    setHasMilestonesError(false);
    const filterCriteria = dataGridMapFilterCriteria([]);

    filterCriteria.workflowUuid = {
      operator: "in",
      value: [workflowUuid || ""],
    };

    filterCriteria.deadlineDate = {
      operator: "neq",
      value: null,
    };

    filterCriteria.isMilestone = {
      operator: "eq",
      value: true,
    };

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };

    try {
      const result = await searchWorkflowTasks({
        paging: {
          limit: 3,
          afterCursor: null,
          beforeCursor: null,
        },
        filter: {
          results: filterCriteria,
        },
        sort: [{ key: "deadlineDate" as "results.deadlineDate", direction: "asc" }],
      });
      if (!isCurrentRequest()) {
        return;
      }
      if (result.status !== Status.Success || result.data === undefined) {
        setHasMilestonesError(true);
        return;
      }

      setUpcomingMilestones(
        // No `project`: the board is already scoped to one workflow object, so naming it on every
        // card would repeat the page heading rather than tell the reader anything.
        result.data.results.map((task) => ({
          key: task.uuid,
          name: task.name,
          deadlineDate: task.deadlineDate ? task.deadlineDate : undefined,
          status: task.status,
          assigneeName: task.assignee?.name ?? undefined,
          onClick: () => {
            setSelectedPanelTask(task);
            setShowWorkflowTaskPanel(true);
          },
        }))
      );
    } catch {
      // A rejected search never reached the status check, which used to leave the card on its
      // skeletons for good.
      if (isCurrentRequest()) {
        setHasMilestonesError(true);
      }
    } finally {
      if (isCurrentRequest()) {
        setIsLoadingMilestones(false);
      }
    }
  };
  useEffect(() => {
    searchUpcomingMilestoneTasks();
  }, [workflowUuid, refreshTasksBoard]);

  const searchTasksProgressKPI = async (toggle = toggleProgress): Promise<void> => {
    let phase = null;
    if (!toggle && selectedWorkflowStage) {
      if (isDealWorkflow) {
        phase = selectedWorkflowStage;
      } else {
        phase = parseProjectPhase(selectedWorkflowStage) ?? selectedWorkflowStage;
      }
    }
    const result = await getAggregateWorkflowTasks({
      phase,
      dealUuid: null,
      projectUuid: null,
      activityUuid: null,
      workflowUuid: workflowUuid || null,
    });

    if (result.status !== Status.Success || result.data === undefined) {
      return;
    }

    setTaskProgress(result.data);
  };

  useEffect(() => {
    searchTasksProgressKPI();
  }, [workflowUuid, refreshTasksBoard, selectedWorkflowStage]);

  const handleToggle = async (newValue: boolean): Promise<void> => {
    setToggleProgress(newValue);
    await searchTasksProgressKPI(newValue);
  };

  const closeEditDatesPanel = (): void => {
    setShowEditDatesPanel(false);
  };

  const handleDealDatesSaved = (): void => {
    setShowEditDatesPanel(false);
    shouldRefreshTasksBoard();
  };

  return {
    projectUuid,
    objectUuid: tasksBoard?.results[0].dealUuid ?? tasksBoard?.results[0].projectUuid ?? undefined,
    objectName: tasksBoard?.results[0].dealName ?? tasksBoard?.results[0].projectDisplayName ?? undefined,
    objectRowVersion: tasksBoard?.results[0].dealRowVersion ?? tasksBoard?.results[0].projectRowVersion ?? undefined,
    workflowUuid,
    tasksBoard,
    workflowOrganisationAccessLevel,
    workflowOrganisations,
    workflow: tasksBoard?.results[0],
    isDealWorkflow,
    dealStages,
    currentDealStage,
    handleDealStageChange,

    showComplete,
    handleShowCompleteChange,

    showAddWorkflowStepPanel,
    setShowAddWorkflowStepPanel,

    showRemoveWorkflowModal,
    setShowRemoveWorkflowModal,

    showEditDatesPanel,
    closeEditDatesPanel,
    handleDealDatesSaved,

    showUpcomingMilestonesPanel,
    setShowUpcomingMilestonesPanel,

    showWorkflowTaskPanel,
    setShowWorkflowTaskPanel,

    selectedPanelTask,
    setSelectedPanelTask,

    projectDates,
    setProjectDates,

    quickActionsMenuOptionsList,
    showQuickActionsMenu,
    setShowQuickActionsMenu,

    refreshTasksBoard,
    shouldRefreshTasksBoard,

    handleCloseWorkflowTaskPanel,
    hasAccessLevelAndCanEditTemplate,
    handleBackClick,
    goBackText,
    upcomingMilestones,
    isLoadingMilestones,
    hasMilestonesError,
    toggleProgress,
    taskProgress,
    onToggle: handleToggle,

    showWorkflowPermissionsPanel,
    setShowWorkflowPermissionsPanel,
  };
};
